<div class="app-layout" [class.drawer-open]="drawerOpen()">
  <!-- Side Drawer -->
  <aside class="drawer">
    <div class="drawer-header">
      <h2>Settings</h2>
      <button class="btn-close-drawer" (click)="drawerOpen.set(false)" title="Close settings">
        ✕
      </button>
    </div>

    <div class="drawer-content">
      <!-- Theme Selection -->
      <div class="drawer-section">
        <h3>Theme</h3>
        <select
          class="theme-dropdown"
          [value]="currentTheme()"
          (change)="currentTheme.set($any($event.target).value)"
        >
          @for (theme of availableThemes; track theme.value) {
            <option [value]="theme.value">{{ theme.label }}</option>
          }
        </select>
      </div>

      <!-- Data Controls -->
      <div class="drawer-section">
        <h3>Data ({{ data().length }} rows)</h3>
        <div class="btn-group-vertical">
          <button class="btn-sm" (click)="addRows(100)">+ 100 Rows</button>
          <button class="btn-sm" (click)="addRows(500)">+ 500 Rows</button>
          <button class="btn-sm btn-reset" (click)="resetData()">Reset</button>
        </div>
      </div>

      <!-- Export -->
      <div class="drawer-section">
        <h3>Export</h3>
        <div class="btn-group-vertical">
          <button class="btn-sm btn-export" (click)="exportCSV()">CSV</button>
          <button class="btn-sm btn-export" (click)="exportJSON()">JSON</button>
        </div>
      </div>

      <!-- Core Features -->
      <div class="drawer-section">
        <h3>Core Features</h3>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableSorting()" (change)="toggleFeature('sorting')" />
          <span>Sorting</span>
        </label>
        <label class="toggle-control" [class.disabled]="!enableSorting()">
          <input type="checkbox" [checked]="enableMultiSort()" (change)="toggleFeature('multiSort')" [disabled]="!enableSorting()" />
          <span>Multi-Sort</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableFiltering()" (change)="toggleFeature('filtering')" />
          <span>Column Filtering</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableGlobalFilter()" (change)="toggleFeature('globalFilter')" />
          <span>Global Filter</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enablePagination()" (change)="toggleFeature('pagination')" />
          <span>Pagination</span>
        </label>
      </div>

      <!-- Selection & Interaction -->
      <div class="drawer-section">
        <h3>Selection & Interaction</h3>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableRowSelection()" (change)="toggleFeature('rowSelection')" />
          <span>Row Selection</span>
        </label>
        <label class="toggle-control" [class.disabled]="!enableRowSelection()">
          <input type="checkbox" [checked]="enableMultiRowSelection()" (change)="toggleFeature('multiRowSelection')" [disabled]="!enableRowSelection()" />
          <span>Multi-Row Selection</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableColumnResizing()" (change)="toggleFeature('columnResizing')" />
          <span>Column Resizing</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableColumnOrdering()" (change)="toggleFeature('columnOrdering')" />
          <span>Column Ordering</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableColumnPinning()" (change)="toggleFeature('columnPinning')" />
          <span>Column Pinning</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableColumnVisibility()" (change)="toggleFeature('columnVisibility')" />
          <span>Column Visibility</span>
        </label>
      </div>

      <!-- Advanced Features -->
      <div class="drawer-section">
        <h3>Advanced Features</h3>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableGrouping()" (change)="toggleFeature('grouping')" />
          <span>Row Grouping</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableRowPinning()" (change)="toggleFeature('rowPinning')" />
          <span>Row Pinning</span>
        </label>
      </div>

      <!-- UI Options -->
      <div class="drawer-section">
        <h3>UI Options</h3>
        <label class="toggle-control">
          <input type="checkbox" [checked]="showToolbar()" (change)="toggleFeature('toolbar')" />
          <span>Show Toolbar</span>
        </label>
        <label class="toggle-control" [class.disabled]="!showToolbar()">
          <input type="checkbox" [checked]="showGlobalFilter()" (change)="toggleFeature('showGlobalFilter')" [disabled]="!showToolbar()" />
          <span>Global Filter in Toolbar</span>
        </label>
        <label class="toggle-control" [class.disabled]="!showToolbar()">
          <input type="checkbox" [checked]="showColumnVisibility()" (change)="toggleFeature('showColumnVisibility')" [disabled]="!showToolbar()" />
          <span>Column Visibility Toggle</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="showPagination()" (change)="toggleFeature('showPagination')" />
          <span>Pagination Controls</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="initialAutoFitColumns()" (change)="toggleFeature('autoFit')" />
          <span>Auto-Fit Columns</span>
        </label>
        <label class="toggle-control">
          <input type="checkbox" [checked]="enableAutoRowHeight()" (change)="toggleFeature('autoRowHeight')" />
          <span>Auto Row Height</span>
        </label>
      </div>

      <!-- Selection Info -->
      @if (enableRowSelection()) {
        <div class="drawer-section selection-info">
          <strong>Selected:</strong> {{ selectedRowsInfo() }}
        </div>
      }
    </div>
  </aside>

  <!-- Main Content Area -->
  <div class="main-content">
    <!-- Header Bar -->
    <header class="app-header">
      <button class="btn-toggle-drawer" (click)="drawerOpen.set(!drawerOpen())" title="Toggle settings">
        <span class="hamburger">☰</span>
      </button>
      <h1>{{ title() }}</h1>
    </header>

    <!-- Tab Navigation -->
    <nav class="tab-nav">
      <button
        class="tab-btn"
        [class.active]="activeTab() === 'ltr'"
        (click)="activeTab.set('ltr')"
      >
        LTR Table (English)
      </button>
      <button
        class="tab-btn"
        [class.active]="activeTab() === 'rtl'"
        (click)="activeTab.set('rtl')"
      >
        RTL Table (العربية)
      </button>
    </nav>

    <!-- LTR Table -->
    @if (activeTab() === 'ltr') {
      <div class="table-wrapper">
        <tanstack-table
          #tableComponent
          [data]="data()"
          [columns]="columns"
          [theme]="currentTheme()"
          [customIcons]="customIcons()"
          [enableSorting]="enableSorting()"
          [enableMultiSort]="enableMultiSort()"
          [enableFiltering]="enableFiltering()"
          [enableGlobalFilter]="enableGlobalFilter()"
          [enablePagination]="enablePagination()"
          [enableRowSelection]="enableRowSelection()"
          [enableMultiRowSelection]="enableMultiRowSelection()"
          [enableColumnResizing]="enableColumnResizing()"
          [enableColumnOrdering]="enableColumnOrdering()"
          [enableColumnPinning]="enableColumnPinning()"
          [enableColumnVisibility]="enableColumnVisibility()"
          [enableGrouping]="enableGrouping()"
          [enableExpanding]="enableGrouping()"
          [enableRowPinning]="enableRowPinning()"
          [initialAutoFitColumns]="initialAutoFitColumns()"
          [initialPageSize]="initialPageSize()"
          [pageSizeOptions]="pageSizeOptions()"
          [showToolbar]="showToolbar()"
          [showGlobalFilter]="showGlobalFilter()"
          [showColumnVisibility]="showColumnVisibility()"
          [showPagination]="showPagination()"
          [enableAutoRowHeight]="enableAutoRowHeight()"
          (rowSelectionChanged)="handleRowSelection($event)"
          (stateChanged)="handleStateChange($event)"
        />
      </div>
    }

    <!-- RTL Table (Arabic) -->
    @if (activeTab() === 'rtl') {
      <div class="table-wrapper rtl-table" dir="rtl" lang="ar">
        <tanstack-table
          [data]="arabicData()"
          [columns]="arabicColumns"
          [theme]="currentTheme()"
          [labels]="arabicLabels"
          [enableSorting]="true"
          [enableMultiSort]="true"
          [enableFiltering]="true"
          [enableGlobalFilter]="true"
          [enablePagination]="true"
          [enableRowSelection]="true"
          [enableMultiRowSelection]="true"
          [enableColumnResizing]="true"
          [enableColumnPinning]="true"
          [enableColumnVisibility]="true"
          [enableGrouping]="true"
          [enableExpanding]="true"
          [initialAutoFitColumns]="true"
          [initialPageSize]="25"
          [pageSizeOptions]="[10, 25, 50, 100]"
          [showToolbar]="true"
          [showGlobalFilter]="true"
          [showColumnVisibility]="true"
          [showPagination]="true"
        />
      </div>
    }
  </div>
</div>
